<template>
    <Detail v-if="data" :logo-on-click="logoOnClick">
        <template v-slot:header>
            <div class="row no-gutters">
                <div class="col col-8">
                    <h2>
                        {{ data.title }}
                        <span class="badge rounded-pill badge-risk" :class="['badge-risk-' + versionRiskLevel(data, version)]"
                            v-b-tooltip.hover :title="hoverTitle(data, version)">
                            {{ $t("risk.levelFull." + versionRiskLevel(data, version)) }}
                        </span>
                    </h2>
                </div>
                <div class="col col-4 text-end">
                    <h2 style="font-weight: normal;">{{ $filters.price(data.robust_price_vat) }}</h2>
                </div>
            </div>
        </template>

        <template #back>
            <router-link :to="{ name: 'contracts', params: { version: version, hash: hash } }" custom v-slot="{ navigate }">
                <button @click="navigate" @keypress.enter="navigate" class="btn btn-inline text-nowrap d-flex align-items-center">
                    <b-icon icon="arrow-left" />&nbsp;{{ $t("contractDetail.nav.contractsList") }}
                </button>
            </router-link>
        </template>

        <template #breadcrumbs>
            <div class="ellipsis">
                <router-link :to="{ name: version, params: { hash: hash } }">{{ data.analysis_name }}</router-link>
                &nbsp;/&nbsp;
                <router-link :to="{ name: 'contracts', params: { version: version, hash: hash } }">{{
                    $t("overview.contracts.title")
                }}</router-link>
                &nbsp;/&nbsp;
                {{ data.title }}
            </div>
        </template>

        <template v-slot:header_small>
            <div class="row no-gutters">
                <div class="col col-12">
                    <h2>
                        {{ data.title }}
                        <span class="badge rounded-pill badge-risk"
                            :class="['badge-risk-' + versionRiskLevel(data, version)]">
                            {{ $t("risk.levelFull." + versionRiskLevel(data, version)) }}
                        </span>
                    </h2>
                </div>
            </div>
        </template>

        <template v-slot:menu>
            <Menu :nav="navigation" :current="currentNav" :hash="hash" :version="version"
                @force-nav="(n) => (forceNav = n)" />
        </template>

        <template v-slot:content>
            <section id="overview_section">
                <Summary v-bind="overviewSettings">
                    <template v-slot:value(supplier)="item">
                        <router-link
                            :to="{ name: 'supplier-detail', params: { hash: hash, version: version, slug: item.value.link } }">{{
                                item.value.name }}</router-link>
                    </template>
                    <template v-slot:value(signatureDate)="item">{{ $filters.formatDate(item.value, "D. M. YYYY")
                    }}</template>
                    <template v-slot:value(price)="item">
                        <span v-if="item.value != null">{{ $filters.price(item.value) }}</span>
                        <span v-else>---</span>
                    </template>
                    <template v-slot:value(paymentsSum)="item">
                        <span>{{ $filters.price(item.value) }}</span>
                    </template>
                    <template v-slot:value(sourceUrl)="item">
                        <ContractSourceLink :source="item.value" :includeSource="false" :right="true"/>
                    </template>
                </Summary>
            </section>

            <section id="risks_section">

                <h4 class="section-title">
                    {{ $t("contractDetail.nav.flags") }}
                </h4>

                <EmptySection v-show="!hasFlatFlagsData" :message="$t('contractDetail.flags.nodata')"></EmptySection>

                <div v-show="hasFlatFlagsData">
                    <div class="row align-items-end">
                        <div class="col col-1 flag_header">
                            {{ $t("contractDetail.flags.header.risk") }}
                        </div>
                        <div class="col col-3 flag_header">
                            {{ $t("contractDetail.flags.header.group") }}
                        </div>
                        <div class="col flag_header">
                            {{ $t("contractDetail.flags.header.name") }}
                        </div>
                    </div>

                    <div v-for="(f, i) in flatFlags" :key="'flat-flag-' + i" class="row align-items-center">
                        <div class="col col-12">
                            <SupplierFlatFlag :flag="f" :version="version" />
                        </div>
                    </div>

                </div>


                <div v-if="otherSupplierFlagSettings" class="row other-flags">
                    <div class="col col-12">
                        <SupplierFlatFlag v-bind="otherSupplierFlagSettings" />
                    </div>
                </div>

            </section>

            <section id="payments_section" v-show="isSectionEnabled('payments')">
                <InvoiceTable :version="version" :hash="hash" :externalData="data" :typeId="data.id" type="contract" />
            </section>

            <section id="nodata_section" v-show="isSectionEnabled('nodata')">
                <h4 class="section-title">
                    {{ $t("contractDetail.nav.nodata") }}
                </h4>
                <EmptySection :message="$t('contractDetail.nodata.description', nodataSections)"></EmptySection>
            </section>

        </template>
    </Detail>
</template>

<script>
import Detail from "@/views/layouts/Detail.vue";
import formattersMixin from "@/plugins/formattersMixin.js";
import Menu from "@/components/Menu.vue";
import Summary from "@/components/Summary.vue";
import EmptySection from "@/components/EmptySection.vue";
import SupplierFlatFlag from "@/components/SupplierFlatFlag.vue";
import ContractSourceLink from "@/components/ContractSourceLink.vue";
import riskMixin from "@/plugins/riskMixin.js";
import dataTableMixin from "@/plugins/dataTableMixin.js";
import tableSettingsMixin from "@/plugins/tableSettingsMixin.js";
import analysisMixin from "@/plugins/analysisMixin.js";
import InvoiceTable from "@/components/InvoiceTable.vue";

export default {
    props: {
        slug: { required: true },
        version: { required: true },
        hash: { required: true }
    },
    name: "supplier-detail",
    components: {
        Detail,
        Menu,
        Summary,
        EmptySection,
        SupplierFlatFlag,
        ContractSourceLink,
        InvoiceTable
    },
    mixins: [formattersMixin, riskMixin, dataTableMixin, tableSettingsMixin, analysisMixin],
    created: function () {
        this.init();
    },
    data: function () {
        return {
            data: null,
            nav: {
                overview: {
                    scrollTo: {
                        el: "#overview_section",
                        offset: -200,
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    label: this.$t("contractDetail.nav.overview")
                },
                risks: {
                    scrollTo: {
                        el: "#risks_section",
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    isVisible: () => true,
                    label: this.$t("contractDetail.nav.risks")
                },
                payments: {
                    scrollTo: {
                        el: "#payments_section",
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    label: this.$t("contractDetail.nav.payments"),
                    isVisible: () => this.data.invoices_count > 0 || this.data.invoices_count_add_info > 0,
                    requiredVersion: "full"
                },
                nodata: {
                    scrollTo: {
                        el: "#nodata_section",
                        onStart: () => (this.currentNav = this.forceNav),
                        onDone: () => (this.forceNav = null)
                    },
                    label: this.$t("contractDetail.nav.nodata"),
                    isVisible: () => false
                }
            },
            currentNav: "overview",
            forceNav: null,
            flags: [],
            detailsLoaded: false,
            overviewSettings: {
                title: "Přehled",
                description: "Základní informace o smlouvě - datum podepsání smlouvy, vysoutěžená a zaplacená cena a zdroj infromací o zakázce.",
                data: []
            },
            flatFlags: [],
            navigation: {},
            nodata: {},
            otherFlags: []
        };
    },
    watch: {
        nodata: function (value) {
            if (!Object.keys(value).length) {
                delete this.navigation["nodata"];
            } else {
               this.navigation["nodata"] = this.nav.nodata;
            }
        },
        "$route.params.slug": function () {
            this.data = null
            this.flatFlags = []
            this.navigation = {}
            this.nodata = {}
            this.otherFlags = []

            this.init()
                .then(() => {
                    this.$refs.payments_data_table.refresh()
                })
        }
    },
    computed: {
        filterDebounce: () => 700,
        nodataSections: function () {
            return {
                sections: Object.values(this.nodata)
                    .map((n) => n.label)
                    .join(", ")
            };
        },
        hasFlatFlagsData: function () {
            return this.flatFlags.length;
        },
        otherSupplierFlagSettings: function () {
            if (!this.otherFlags.length) {
                return null
            }

            var worst = this.otherFlags.reduce((max, flag) => {
                return max.risk > flag.risk ? max : flag;
            })

            let settings = {
                flag: {
                    flag_type_group_name: this.$t('contractDetail.flatFlags.other.group'),
                    flag_type_name: this.$t('contractDetail.flatFlags.other.name', { count: this.otherFlags.length }),
                    description: this.$t('contractDetail.flatFlags.other.desc'),
                    risk: worst.risk
                },
                version: this.version
            }

            settings.flag[`risk_level_${this.version}`] = worst[`risk_level_${this.version}`]

            return settings
        }
    },
    methods: {
        init: function () {
            return new Promise((resolve, reject) => {
                this.loadData()
                    .then(() => {
                        this.overviewSettings.data = this.buildOverviewData();

                        this.loadFlatFlagsContract();
                        this.loadFlatFlagsSupplier();

                        this.buildNavigation();

                        window.removeEventListener("scroll", this.onScroll);
                        window.addEventListener("scroll", this.onScroll);

                        this.$root.$emit("set-page-title", this.$t("titleDetail", { name: this.data.title }))

                        resolve()
                    })
                    .catch(e => reject(e))
            })
        },
        onScroll: function () {
            if (this.forceNav) {
                return;
            }

            Object.keys(this.navigation).forEach((k) => {
                var n = this.navigation[k];

                if (n.scrollTo) {
                    var el = document.querySelector(n.scrollTo.el);
                    if (el) {
                        var top = el.getBoundingClientRect().top;
                        if (top > 0 && top <= 100) {
                            this.currentNav = k;
                        }
                    }
                }
            });
        },
        loadData: function () {
            return new Promise((resolve, reject) => {
                var self = this;

                var params = {};

                params[`${this.version}_hash`] = this.hash;

                const endpoint = this.$route.name === "icontract-detail" ? "icontract" : "contract";
                this.$store.getters.api
                    .get(endpoint + "/" + this.slug, { params: params })
                    .then((response) => {
                        self.data = response.data;
                        resolve();
                    })
                    .catch((e) => {
                        reject(e);
                        self.data = null;
                    });
            });
        },
        loadFlatFlagsContract: function () {
            var params = {
                format: "json",
                contract: this.data.id,
                flag_level: "contract",
                order_by: "-risk",
                bidders_is_null: true,
                limit: 0
            };

            params[`${this.version}_hash`] = this.hash;

            return this.$store.getters.api
                .get("flag/", { params: params })
                .then((response) => {
                    response.data.data.forEach(
                        (obj) => { this.flatFlags.push(obj); }
                    );
                    this.flatFlags.sort(function compareFn(firstEl, secondEl) {
                        var fEl = parseFloat(firstEl.risk);
                        var sEl = parseFloat(secondEl.risk);
                        if (fEl > sEl) {
                            return -1;
                        }
                        if (fEl < sEl) {
                            return 1;
                        }
                        return 0;
                    });
                })
                .catch((e) => {
                    console.dir(e);
                    this.flatFlags = [];
                });
        },
        loadFlatFlagsSupplier: function () {
            var params = {
                format: "json",
                supplier: this.data.supplier_contract.id,
                flag_level: "supplier,analysis_supplier",
                order_by: "-risk",
                limit: 0
            };

            params[`${this.version}_hash`] = this.hash;

            return this.$store.getters.api
                .get("flag/", { params: params })
                .then((response) => {
                    response.data.data.forEach(
                        (obj) => { this.flatFlags.push(obj); }
                    );
                    this.flatFlags.sort(function compareFn(firstEl, secondEl) {
                        var fEl = parseFloat(firstEl.risk);
                        var sEl = parseFloat(secondEl.risk);
                        if (fEl > sEl) {
                            return -1;
                        }
                        if (fEl < sEl) {
                            return 1;
                        }
                        return 0;
                    });
                })
                .catch((e) => {
                    console.dir(e);
                    this.flatFlags = [];
                });
        },
        onNav: function (value) {
            this.$scrollTo(this.nav[value.section].scrollTo.el, "body", this.nav[value.section].scrollTo);
        },
        isSectionEnabled: function (section) {
            return Object.prototype.hasOwnProperty.call(this.navigation, section);
        },
        buildNavigation: function () {
            this.navigation = {};
            this.nodata = {};
            Object.keys(this.nav).forEach((n) => {
                let nav = { ...this.nav[n] };

                var verified = true
                if (!this.verifyVersion(this.version, nav.requiredVersion)) {
                    delete nav["scrollTo"];
                    delete nav["to"];
                    verified = false
                }

                if (!Object.prototype.hasOwnProperty.call(nav, "isVisible") || nav.isVisible()) {
                    this.navigation[n] = nav;
                } else if (nav.nodataSection && verified) {
                    this.nodata[n] = nav;
                }
            });
        },
        buildOverviewData: function () {
            return {
                supplier: {
                    label: this.$t("contractDetail.overviewSummary.supplier"),
                    value: this.data.supplier_contract
                },
                signatureDate: {
                    label: this.$t("contractDetail.overviewSummary.signatureDate"),
                    value: this.data.signature_date
                },
                price: {
                    label: this.$t("contractDetail.overviewSummary.price"),
                    value: this.data.robust_price_vat
                },
                paymentsSum: {
                    label: this.$t("contractDetail.overviewSummary.paymentsSum"),
                    value: this.data.payment_sum_vat,
                    isVisible: () => this.data.payment_sum_vat > 0
                },
                sourceUrl: {
                    label: this.$t("contractDetail.overviewSummary.sourceUrl"),
                    value: this.data.source_url
                }
            };
        },
        logoOnClick: function () {
            this.$router.push({ name: this.version, params: { hash: this.hash } })
        },
        hoverTitle: function (data, version) {
            var level = this.versionRiskLevel(data, version);
            return this.$t(`risk.levelFull.${level}`) + (level != 'no' ? ` ${this.formatPercentage(data.risk, 2)}` : '')
        }
    }
};
</script>

<style lang="scss">
@use "@/scss/custom";

#menu {
    margin-bottom: 50px;

    button {
        text-align: left;
        background-color: white;
        padding: 0.6rem 1.5rem;
        padding-right: 0;

        &.active {
            background-color: custom.$background-light;
        }

        &:hover {
            background-color: custom.$light;
        }

        &[disabled] {
            background-color: transparent;
        }

        .premium_label {
            float: right;
        }
    }
}


.section-title {
    font-size: 20px;
    font-weight: 700;
    color: custom.$body-color;
    margin-bottom: 20px;
}

#payments_section {
    .b-table-has-details td {
        border-bottom: none;
    }

    .b-table-details {
        background-color: initial;

        td {
            padding-top: 0;
        }
    }
}

.empty-section~.other-flags {
    margin-top: 10px;
}

.overview_description {
    width: 80%;
}
</style>
